// Providers
import { Web3Provider } from '@ethersproject/providers'
import type { ExternalProvider } from '@ethersproject/providers'
import type { Connector } from 'wagmi'
import { authApi } from '../api/axios'
import getApiHeaders from './getApiHeaders'

export type SignableWallet = Web3Provider | Connector

function logWeb3(phase: string, detail?: unknown): void {
  if (!import.meta.env.DEV) return
  // eslint-disable-next-line no-console
  console.warn(`[mprolab/web3] ${phase}`, detail === undefined ? '' : detail)
}

function connectorLabel(p: SignableWallet): string {
  if (p instanceof Web3Provider) return 'Web3Provider'
  if (p && typeof p === 'object' && 'id' in p && typeof (p as Connector).id === 'string') {
    return (p as Connector).id
  }
  return 'unknown'
}

const getSignature = async (account: string, wallet: SignableWallet) => {
  if (!account || !wallet) {
    throw new Error('No account or provider data provided')
  }

  logWeb3('getSignature:start', { account, connector: connectorLabel(wallet) })

  const signatureResponse = await authApi.get(`signature/hash`, {
    headers: getApiHeaders({ account })
  })
  const { hash } = signatureResponse.data
  const walletAddress = account.toLowerCase()
  const verifyMessage = `Please sign to let us verify\nthat you are the owner of this address\n${walletAddress}\n\nRequest ID ${hash}`

  try {
    const web3 =
      wallet instanceof Web3Provider
        ? wallet
        : new Web3Provider((await wallet.getProvider()) as ExternalProvider, 'any')
    const signer = web3.getSigner(account)
    const signature = await signer.signMessage(verifyMessage)
    logWeb3('getSignature:ok', { via: 'Web3Provider.signMessage' })
    return { walletAddress, signature, hash }
  } catch (e) {
    logWeb3('getSignature:sign-failed', e)
    throw e
  }
}

export default getSignature
